import { useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { INSTALLATION_APPOINTMENT_TIME_SLOT_ID } from '../../../common/constants/ProductOfferingConstants.js';
import { toDacInstallationDate } from '../../../common/installationDatesApiCoCo.js';
import { getAvailableDates, normalizeDatePickerValue } from '../../../common/utils/dateUtilsCoCo.js';
import { shouldShowInstallationDateSelection } from '../productConfig/productConfigRegistryCoCo.js';
import type { AvailableInstallationDatesResponseItem } from '../../../common/types/installationDatesCoCo.js';
import type { ShoppingBasketType } from '../../../common/types/shoppingBasketCoCo.js';
import type { PlaceOrderFormDataCoCo, PlaceOrderItemUnitCoCo } from '../placeOrderTypesCoCo.js';
import type { Control, UseFormGetValues, UseFormSetValue } from 'react-hook-form';

interface UseInstallationSelectionInvalidationOptions {
  basket: ShoppingBasketType;
  control: Control<PlaceOrderFormDataCoCo>;
  getValues: UseFormGetValues<PlaceOrderFormDataCoCo>;
  setValue: UseFormSetValue<PlaceOrderFormDataCoCo>;
  invalidateInstallationAvailability: () => void;
  currentAvailabilityResults?: AvailableInstallationDatesResponseItem[];
}

export const getInstallationConfigurationKey = (
  basket: ShoppingBasketType,
  preferredInstallationDate: string | undefined,
  placeOrderItemUnits: PlaceOrderItemUnitCoCo[] | undefined
): string => {
  const installationAvailabilityItems = basket.productOfferings
    .filter(shouldShowInstallationDateSelection)
    .map(item => ({
      id: item.id,
      cartItemId: item.cartItemId,
      quantity: item.quantity,
      productOfferingType: item.productOfferingType,
      planProductOfferingId: item.planProductOffering?.id,
      extensions: item.extensions?.filter(extension => extension.name !== INSTALLATION_APPOINTMENT_TIME_SLOT_ID),
      productOfferingRuntimeCharacteristic: item.productOfferingRuntimeCharacteristic,
      bundleAddonGroupSelections: item.bundleAddonGroup?.map(group => ({
        id: group.id,
        selectedOfferingIds: group.productOffering
          ?.filter(offering => offering.isSelected)
          .map(offering => offering.id),
      })),
    }));
  const installationAvailabilityCartItemIds = new Set(installationAvailabilityItems.map(item => item.cartItemId));
  const installationAvailabilityUnits = (placeOrderItemUnits ?? [])
    .filter(unit => installationAvailabilityCartItemIds.has(unit.basketItemId))
    .map(unit => ({
      basketItemId: unit.basketItemId,
      basketItemIndex: unit.basketItemIndex,
      installationContact: unit.installationContact,
      additionalParameters: unit.additionalParameters,
      productOfferAddons: unit.productOfferAddons,
      addonRuntimeCharacteristics: unit.addonRuntimeCharacteristics,
    }));

  return JSON.stringify({
    preferredInstallationDate: preferredInstallationDate ? toDacInstallationDate(preferredInstallationDate) : undefined,
    installationAvailabilityItems,
    installationAvailabilityUnits,
  });
};

const getInstallationAvailabilityConfigurationKey = (
  basket: ShoppingBasketType,
  placeOrderItemUnits: PlaceOrderItemUnitCoCo[] | undefined
): string => getInstallationConfigurationKey(basket, undefined, placeOrderItemUnits);

export const useInstallationSelectionInvalidationCoCo = ({
  basket,
  control,
  getValues,
  setValue,
  invalidateInstallationAvailability,
  currentAvailabilityResults,
}: UseInstallationSelectionInvalidationOptions): void => {
  const preferredInstallationDate = useWatch({ control, name: 'products.preferredInstallationDate' });
  const placeOrderItemUnits = useWatch({ control, name: 'products.placeOrderItemUnits' });
  const configurationKey = getInstallationAvailabilityConfigurationKey(basket, placeOrderItemUnits);
  const preferredInstallationDateKey = preferredInstallationDate
    ? toDacInstallationDate(preferredInstallationDate)
    : undefined;
  const selectedDateIsAvailable = currentAvailabilityResults
    ? getAvailableDates(currentAvailabilityResults).includes(normalizeDatePickerValue(preferredInstallationDate ?? ''))
    : false;
  const previousConfigurationKey = useRef(configurationKey);
  const previousPreferredInstallationDateKey = useRef(preferredInstallationDateKey);
  const invalidateAvailability = useRef(invalidateInstallationAvailability);
  invalidateAvailability.current = invalidateInstallationAvailability;

  useEffect(() => {
    const configurationChanged = configurationKey !== previousConfigurationKey.current;
    const preferredDateChanged = preferredInstallationDateKey !== previousPreferredInstallationDateKey.current;
    if (!configurationChanged && !preferredDateChanged) {
      return;
    }

    previousConfigurationKey.current = configurationKey;
    previousPreferredInstallationDateKey.current = preferredInstallationDateKey;
    if (!configurationChanged && selectedDateIsAvailable) {
      const existingTimeSlotIds = getValues('products.installationAppointmentTimeSlotIds');
      if (existingTimeSlotIds && Object.keys(existingTimeSlotIds).length > 0) {
        setValue('products.installationAppointmentTimeSlotIds', {}, { shouldDirty: true, shouldValidate: true });
      }
      return;
    }
    invalidateAvailability.current();

    const selectedTimeSlotIds = getValues('products.installationAppointmentTimeSlotIds');
    if (selectedTimeSlotIds && Object.keys(selectedTimeSlotIds).length > 0) {
      setValue('products.installationAppointmentTimeSlotIds', {}, { shouldDirty: true, shouldValidate: true });
    }
  }, [configurationKey, getValues, preferredInstallationDateKey, selectedDateIsAvailable, setValue]);
};
